import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { ExchangeRate } from '../../types';
import '../../styles/ExchangeRateForm.css';

interface ExchangeRateFormProps {
  exchangeRate: ExchangeRate | null;
  onSave: (data: any) => void;
  onCancel: () => void;
}

const ExchangeRateForm: React.FC<ExchangeRateFormProps> = ({ exchangeRate, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    effective_date: new Date(),
    vnd_to_usd: 23500, // Default rate
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (exchangeRate) {
      setFormData({
        effective_date: new Date(exchangeRate.effective_date),
        vnd_to_usd: exchangeRate.vnd_to_usd,
      });
    }
  }, [exchangeRate]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.vnd_to_usd || formData.vnd_to_usd <= 0) {
      newErrors.vnd_to_usd = 'Exchange rate must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: name === 'vnd_to_usd' ? parseFloat(value) : value 
    }));
  };

  const handleDateChange = (date: Date) => {
    setFormData(prev => ({ ...prev, effective_date: date }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validate()) {
      // Format date for API
      const submissionData = {
        ...formData,
        effective_date: formData.effective_date.toISOString().split('T')[0]
      };
      
      onSave(submissionData);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-header">
          <h3 className="modal-title">{exchangeRate ? 'Edit Exchange Rate' : 'Create Exchange Rate'}</h3>
          <button className="close-button" onClick={onCancel}>&times;</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="effective_date">Effective Date</label>
              <DatePicker
                selected={formData.effective_date}
                onChange={handleDateChange}
                dateFormat="yyyy-MM-dd"
                className="form-control"
                id="effective_date"
              />
              {errors.effective_date && <div className="error">{errors.effective_date}</div>}
            </div>
            
            <div className="form-group">
              <label htmlFor="vnd_to_usd">Exchange Rate (VND per 1 USD)</label>
              <input
                type="number"
                name="vnd_to_usd"
                id="vnd_to_usd"
                value={formData.vnd_to_usd}
                onChange={handleChange}
                className="form-control"
                step="0.01"
                min="0"
              />
              {errors.vnd_to_usd && <div className="error">{errors.vnd_to_usd}</div>}
              
              <div className="conversion-preview">
                <p>1 USD = {formData.vnd_to_usd.toLocaleString()} VND</p>
                <p>1 VND = {(1 / formData.vnd_to_usd).toFixed(8)} USD</p>
              </div>
            </div>

            <div className="info-block">
              <p><i className="fas fa-info-circle"></i> The exchange rate will be used for all records created on or after the effective date.</p>
            </div>
          </div>
          
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            <button type="submit" className="btn btn-primary">Save Exchange Rate</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExchangeRateForm;
